export interface FrontendMenuSettings {
    show_admin?: boolean;
    show_team?: boolean;
    show_branding?: boolean;
    [key: string]: boolean | undefined;
}

export interface FrontendFeatureSettings {
    sharing?: boolean;
    smart_templates?: boolean;
    template_editing?: boolean;
    template_suggestions?: boolean;
    search_recordings?: boolean;
    [key: string]: boolean | undefined;
}

export interface FrontendSettings {
    version?: number;
    features?: FrontendFeatureSettings;
    menu?: FrontendMenuSettings;
}

export interface UserCapabilities {
    can_share?: boolean;
    is_admin?: boolean;
    is_org_admin?: boolean;
    [key: string]: boolean | undefined;
}

export interface User {
    id: string;
    username: string;
    level: string;
    email?: string;
    canShare?: boolean;
    user_type?: string;
    is_admin?: boolean;
    is_org_admin?: boolean;
    organization_id?: number;
    group_id?: number;
    capabilities?: UserCapabilities;
    frontend_settings?: FrontendSettings;
}

export interface Tag {
    id: number;
    name: string;
    description?: string;
    color: string;
    file_count?: number;
    created_at?: string;
    updated_at?: string;
}

export type SharedPermissionLevel = 'view' | 'edit' | 'download' | 'manage' | 'owner';

export interface Recording {
    id: string;
    name: string;
    status: ProcessingStatus;
    duration: string;
    recordingDateTime?: string;
    createdAt: string;
    updatedAt: string;
    audioUrl?: string;
    tags?: Tag[];
    canEdit?: boolean;
    canDownload?: boolean;
    canManage?: boolean;
    sharedPermission?: SharedPermissionLevel | null;
}

export type RecordingSearchStatus = 'ready' | 'building';

export type RecordingSearchSort = 'relevance' | 'date_desc' | 'date_asc';

export type RecordingSearchMatchKind = 'title' | 'transcript' | 'summary' | 'supporting_document' | 'tag';

export interface RecordingSearchMatch {
    kind: RecordingSearchMatchKind;
    // Plain text met alleen <mark>…</mark>-markering; nooit als ruwe HTML renderen
    snippet: string;
    // BM25-score, lager = relevanter; null bij title-/tagmatches via metadata
    score: number | null;
    // Alleen aanwezig bij kind === 'summary'; bruikbaar om direct het juiste verslag te openen
    meetingType?: string;
}

export interface RecordingSearchResult {
    recording: Recording;
    matches: RecordingSearchMatch[];
}

export interface RecordingSearchResponse {
    status: RecordingSearchStatus;
    totalCount: number;
    results: RecordingSearchResult[];
}

export enum ProcessingStatus {
    NOT_UPLOADED = 'NOT_UPLOADED',
    UPLOADING = 'UPLOADING',
    NOT_STARTED = 'NOT_STARTED',
    NOT_PROCESSED = 'NOT_PROCESSED',
    QUEUED = 'QUEUED',
    PROCESSING = 'PROCESSING',
    PROCESSED = 'PROCESSED',
    ERROR = 'Error',
}

export interface AdditionalSettings {
    goal: string;
    perspective: string;
    audience: string;
    style: string;
    tense: string;
    structure: string;
    // Verslaglengte, canonieke backend-sleutel "lengte".
    // Leeg = automatisch; verder "beknopt"|"normaal"|"uitgebreid", "400", "600-800", "15%".
    length: string;
}

export interface MeetingType {
    id: number;
    // Sleutel voor de template-endpoints (/template/{ref}/…). Ontbreekt zolang
    // de backend nog geen uuid meestuurt; gebruik dan getTemplateRef().
    uuid?: string;
    // Sleutel voor de verslag-endpoints (meeting_type). Bij persoonlijke
    // templates lijkt dit ook op een uuid, maar het is níet de template-uuid.
    name: string;
    title: string;
    description: string;
    type: string;
    is_default: boolean;
    is_org_wide: boolean;
    content?: string; // Template content (markdown)
    smartTemplateSchema?: SmartTemplateSchema | null;
    additionalSettings?: AdditionalSettings | null;
}

export interface SmartTemplateField {
    code: string;
    label: string;
    description?: string;
    required: boolean;
    multiline: boolean;
    exportKey?: string;
}

export interface SmartTemplateSchema {
    version: number;
    syntax: 'field_markers';
    fields: SmartTemplateField[];
}

export interface ExtractedFieldItem {
    code: string;
    label: string;
    value: string | string[] | null;
    description?: string;
    required: boolean;
    multiline: boolean;
    exportKey?: string;
}

export interface TemplatePreview {
    content: string;
    title?: string;
    description?: string;
    additionalSettings?: any; // JSON string of object met AI-gegenereerde settings
    smartTemplateSchema?: SmartTemplateSchema | null;
}

// Door de backend herkende lengtelimieten per sectie (uit [placeholder]-annotaties in de templatetekst)
export interface SectionLengthLimit {
    section: string;
    description: string;
    unit: string;
    minValue: number | null;
    maxValue: number | null;
    maxWordsEquivalent: number | null;
}

export interface SectionLengthLimits {
    limits: SectionLengthLimit[];
    sectionsTotal: number;
    sectionsWithLimit: number;
    fullyAnnotated: boolean;
    totalMaxWords: number | null;
}

export type TemplateDraftStatus = 'draft' | 'applied' | 'snapshot';

export interface TemplateDraft {
    id: number;
    templateId?: number;
    // Het template waar dit concept echt bij hoort; kan afwijken van de uuid in
    // het pad (persoonlijke kopie van een standaardtemplate).
    templateUuid?: string;
    revisionNumber: number;
    title?: string;
    description?: string;
    revisionReason?: string;
    status: TemplateDraftStatus;
    createdAt: string;
    createdBy?: string;
    content?: string;
    smartTemplateSchema?: SmartTemplateSchema | null;
    additionalSettings?: AdditionalSettings | null;
    sectionLengthLimits?: SectionLengthLimits | null;
}

export type TemplateComponentMode = 'preview' | 'content' | 'metadata' | 'system' | 'task' | 'auto';
export type TemplateComponent = 'system_message' | 'task' | 'content' | 'title' | 'description';

export type TemplateSuggestionStatus = 'open' | 'accepted' | 'applied' | 'rejected' | 'failed';

export interface TemplateSuggestion {
    id: number;
    template_id: number;
    template_name?: string;
    template_title?: string;
    template?: { id: number; uuid?: string; name: string; title: string; description?: string } | null;
    status: TemplateSuggestionStatus;
    original_feedback: string;
    normalized_instruction: string;
    target_components: TemplateComponent[];
    confidence?: number;
    reasoning?: string;
    resolution_note?: string;
    created_at: string;
    updated_at: string;
}

export interface SupportingDocumentSummary {
    id: number;
    filename: string;
    content_type: string;
    description?: string;
    file_progress_id: number;
}

export interface Summary {
    id: string;
    fileId: string;
    // Naam van het template waaronder het verslag is opgeslagen; altijd bruikbaar.
    meetingType: string;
    // Sleutel voor de verslag-endpoints. null bij oudere verslagen of een
    // verwijderd template, undefined als de backend hem (nog) niet meestuurt.
    templateUuid?: string | null;
    content: string;
    sourceContent?: string;
    title: string;
    createdAt: string;
    updatedAt: string;
    additionalRecordings?: string[];
    templateRevisionId?: number;
    supportingDocuments?: SupportingDocumentSummary[];
    extractedFields?: Record<string, unknown> | null;
    extractedFieldItems?: ExtractedFieldItem[];
    extractionErrors?: string[];
    templateSchema?: SmartTemplateSchema | null;
}

// Wat een verslag-endpoint nodig heeft: de template-uuid als die bekend is, en
// de naam als fallback (oudere verslagen, verwijderd template, oude backend).
export type SummaryRef = Pick<Summary, 'meetingType' | 'templateUuid'>;

export interface AuthTokens {
    accessToken: string;
    refreshToken: string;
    expiresAt: Date;
}

export interface ApiResponse<T = any> {
    data?: T;
    error?: string;
    status: number;
}

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: Date;
    canAddToSummary?: boolean;
    addedToSummary?: boolean;
    status?: 'sending' | 'delivered' | 'error';
    isAddingToSummary?: boolean;
}

export interface TranscriptLine {
    speaker: string;
    text: string;
    timestamp: number;
}

export interface SupportingDocument {
    id: number;
    filename: string;
    content_type: string;
    description?: string;
    file_size: number;
    created_at: string;
    content_length: number;
    content?: string; // Voor volledige content wanneer opgehaald
}

export type FileShareTargetType = 'user' | 'group';
export type FileSharePermission = 'view' | 'edit' | 'download' | 'manage';

export interface FileShare {
    id: number;
    targetType: FileShareTargetType;
    targetUserUuid?: string | null;
    targetGroupId?: number | null;
    targetId?: number | null;
    permission: FileSharePermission;
    expiresAt?: string | null;
    note?: string | null;
    status?: string;
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
    revokedAt?: string | null;
    targetName?: string;
    targetEmail?: string;
    targetIdentifier?: string;
    revokedBy?: number | string | null;
}

export interface CreateFileShareInput {
    targetType: FileShareTargetType;
    targetUserUuid?: string;
    targetGroupId?: number;
    permission?: FileSharePermission;
    expiresAt?: string | null;
    note?: string | null;
}

export interface GroupMember {
    userUuid: string;
    username: string;
    email?: string;
}

export interface TeamGroupInfo {
    id: number;
    name: string;
}

export interface TeamGroupUsersResponse {
    group: TeamGroupInfo;
    members: GroupMember[];
}

export interface UserPreferences {
    personalContext?: string;
    phoneNumber?: string;
    authMethod: 'email' | 'sms';
    retentionDays?: number | null;
    organizationRetentionDays?: number | null;
    effectiveRetentionDays?: number | null;
    /** Eigen bewaartermijn voor verslagen; null = volgt retentionDays.
     *  Staat los van retentionDays: de verslagtermijn wist alleen de verslagen,
     *  de opname en het transcript blijven tot de opnametermijn. */
    summaryRetentionDays?: number | null;
    organizationSummaryRetentionDays?: number | null;
    effectiveSummaryRetentionDays?: number | null;
}

/**
 * Productupdates / changelog. Voor nu lokaal gemockt (zie updatesService),
 * later serverside opgehaald — daarom bewust een serialiseerbaar, plat model.
 */
export type UpdateCategory = 'nieuw' | 'verbeterd' | 'aankondiging';

/**
 * Publicatiekanaal. Een update kan op meerdere kanalen tegelijk verschijnen.
 * Deze web-app toont alleen updates die 'web' bevatten (zie updatesService).
 */
export type UpdateChannel = 'web' | 'app' | 'desktop';

export interface UpdateBlock {
    type: 'heading' | 'paragraph' | 'list';
    /** Voor heading & paragraph */
    text?: string;
    /** Voor list */
    items?: string[];
}

/**
 * Exportconfiguratie zoals geleverd door
 * GET /export/export_configurations/{file_progress_id}/{meeting_template_name}.
 * Bepaalt welke externe exportopties in het deelmenu verschijnen.
 */
export interface ExportConfiguration {
    id: string;
    /** Bijv. "CUSTOM_WEBHOOK", "HUBSPOT", "NOTION", "EDU_V" */
    targetPlatform: string;
    /** Toonbare naam van de configuratie, gebruikt als menulabel */
    name: string;
    /**
     * Bij EDU_V en M_FILES: bepaalt welke flow getoond wordt. Er is geen los
     * endpoint voor, dus deze waarde komt uit configuration_details van de
     * configuratielijst en moet daar bewaard blijven.
     */
    exportMethod?: EduVExportMethod | MFilesExportMethod;
    /** HubSpot-instellingen uit configuration_details, nodig voor de deelflow. */
    hubspot?: {
        targetObjectType: string;
        createIfNotFound: boolean;
    };
}

/** Zoekresultaat uit HubSpot; uitsluitend in het huidige deelvenster bewaren. */
export interface HubSpotSearchResult {
    id: string;
    name: string;
}

/**
 * PROGRESS plaatst een signaal met een deeplink (verslaginhoud blijft in
 * Notizy); LAS_WEBHOOK stuurt het volledige verslag naar het
 * leerlingadministratiesysteem.
 */
export type EduVExportMethod = 'PROGRESS' | 'LAS_WEBHOOK';

/**
 * Antwoord van de synchrone export-pushes (custom webhook push, Praktijkdata
 * link). De backend vertaalt de respons van het externe systeem naar deze
 * vorm: bij succes een letterlijk te tonen bericht met optionele
 * "Openen"-URL, bij een fout een foutmelding.
 */
export interface ExportPushResult {
    success: boolean;
    /** Letterlijk aan de gebruiker te tonen melding */
    message: string;
    /** Optionele link, tonen als "Openen"-knop (nieuw tabblad) */
    url?: string;
}

/**
 * Eén rij uit de Praktijkdata-afsprakenzoekactie. Bevat cliëntnamen uit het
 * EPD: alleen in-memory gebruiken, nooit persisteren of loggen.
 */
export interface PraktijkdataSearchResult {
    /** "{clientId}.{dossierId}" — letterlijk doorgeven als platform_object_id */
    id: string;
    clientId: number;
    dossierId: number;
    name: string;
    afspraakId?: number;
    /** Lokale praktijktijd zonder tijdzone-offset; letterlijk tonen, niet naar UTC converteren */
    datumTijd?: string;
    afspraaktype?: string;
    status?: string;
}

export interface PraktijkdataSearchResponse {
    results: PraktijkdataSearchResult[];
    /** Altijd false voor Praktijkdata: nooit een "nieuw aanmaken"-knop tonen */
    canCreate: boolean;
    /** Config-id om mee te sturen met de link-call */
    configId: string;
}

/**
 * Eén leerling uit de Edu-V-zoekactie. Bevat leerlingnamen uit het
 * leerlingadministratiesysteem: alleen in-memory gebruiken, nooit persisteren
 * of loggen.
 */
export interface EduVSearchResult {
    /**
     * ECK iD in de vorm "{idType}:{waarde}". Direct herleidbaar tot één
     * leerling: nooit tonen, loggen of parsen — alleen letterlijk doorgeven
     * als platform_object_id.
     */
    id: string;
    name: string;
    /** Het LAS onderscheidt naamgenoten hiermee; tonen zodra gevuld */
    alias?: string;
}

export interface EduVSearchResponse {
    results: EduVSearchResult[];
    /** true = meer dan 200 treffers; gebruiker moet de zoekopdracht verfijnen */
    truncated: boolean;
    /** Altijd false voor Edu-V: het LAS is de bron, nooit een "aanmaken"-knop tonen */
    canCreate: boolean;
    /** Config-id om mee te sturen met de link-call */
    configId: string;
}

/**
 * Eén klant uit de Mijn Kantoor-zoekactie. Bevat klantnamen uit het
 * kantoordossier: alleen in-memory gebruiken, nooit persisteren of loggen.
 */
export interface MijnKantoorSearchResult {
    /** Mijn Kantoor-klant-id; letterlijk doorgeven als platform_object_id */
    id: string;
    name: string;
    /** Klantnummer in Mijn Kantoor */
    number?: string;
    /** business | person | internal | supplier */
    type?: string;
    city?: string;
    /** Deeplink naar de klantkaart in het Mijn Kantoor-portaal */
    webUrl?: string;
}

export interface MijnKantoorSearchResponse {
    results: MijnKantoorSearchResult[];
    /** true = meer dan 50 treffers; gebruiker moet de zoekterm verfijnen */
    truncated: boolean;
    /** Altijd false voor Mijn Kantoor: nooit een "nieuw aanmaken"-knop tonen */
    canCreate: boolean;
    /** Config-id om mee te sturen met de link-call */
    configId: string;
}

/**
 * Eén relatie uit de AFAS-zoekactie. Bevat relatienamen uit AFAS Profit:
 * alleen in-memory gebruiken, nooit persisteren of loggen.
 */
export interface AfasSearchResult {
    /** AFAS-relatie-id; letterlijk doorgeven als platform_object_id */
    id: string;
    name: string;
    /**
     * Vrije key/value-paren, alleen aanwezig als de admin extra_velden heeft
     * geconfigureerd. De keys zijn veldnamen uit de AFAS-omgeving en dus niet
     * voorspelbaar: generiek renderen, nooit hardcoden.
     */
    details?: Record<string, string>;
}

export interface AfasSearchResponse {
    results: AfasSearchResult[];
    /** true = meer dan 50 treffers; gebruiker moet de zoekterm verfijnen */
    truncated: boolean;
    /** Altijd false voor AFAS: nooit een "nieuw aanmaken"-knop tonen */
    canCreate: boolean;
    /** Config-id om mee te sturen met de link-call */
    configId: string;
}

/**
 * Documenttype dat het zaaktype van de gevonden zaak toestaat (ZGW Catalogi
 * API). De uuid gaat mee als tweede deel van platform_object_id; alleen de
 * omschrijving tonen.
 */
export interface ZgwInformatieobjecttype {
    uuid: string;
    omschrijving: string;
    vertrouwelijkheidaanduiding?: string;
    /** true = door de backend voorgesteld als default voor gespreksverslagen */
    voorgesteld: boolean;
}

/**
 * Eén zaak uit de ZGW-zoekactie (zaaknummer is uniek, dus 0 of 1 resultaat).
 * Zaakomschrijvingen kunnen persoonsgegevens bevatten: alleen in-memory
 * gebruiken, nooit persisteren of loggen.
 */
export interface ZgwSearchResult {
    /** Zaak-UUID; eerste deel van platform_object_id */
    id: string;
    /** Zaaknummer zoals de gemeente dat hanteert, bv. "ZAAK-2026-0000000001" */
    identificatie: string;
    omschrijving?: string;
    /** ISO-datum (YYYY-MM-DD) */
    startdatum?: string;
    zaaktypeOmschrijving?: string;
    vertrouwelijkheidaanduiding?: string;
    /** Leeg = het zaaktype staat geen documenttypen toe; koppelen kan dan niet */
    informatieobjecttypen: ZgwInformatieobjecttype[];
}

export interface ZgwSearchResponse {
    results: ZgwSearchResult[];
    /** Altijd false voor ZGW: zaken aanmaken vanuit Notizy is bewust niet ondersteund */
    canCreate: boolean;
    /** Config-id om mee te sturen met de link-call */
    configId: string;
    /** Toegestane vertrouwelijkheidsniveaus voor het document, in vaste volgorde */
    vertrouwelijkheidaanduidingen: string[];
    /** Voorselectie voor de vertrouwelijkheids-dropdown */
    vertrouwelijkheidaanduidingDefault?: string;
}

/**
 * DOCUMENT_LINKED laat de gebruiker eerst een object in de vault zoeken en
 * koppelt het verslag daaraan; DOCUMENT stuurt het verslag zonder zoekstap
 * naar de vault. De backend weigert een push op een DOCUMENT_LINKED-configuratie
 * met een 400, dus deze waarde bepaalt welk endpoint we aanroepen.
 */
export type MFilesExportMethod = 'DOCUMENT_LINKED' | 'DOCUMENT';

/**
 * Eén object (klant, relatie, project) uit de M-Files-zoekactie. Bevat
 * klantnamen uit de vault: alleen in-memory gebruiken, nooit persisteren of
 * loggen.
 */
export interface MFilesSearchResult {
    /** "{objectType}.{objectId}"; letterlijk doorgeven als platform_object_id */
    id: string;
    name: string;
    /** Het nummer waarmee de vault het object toont */
    displayId?: string;
    /** ISO-tijdstempel van de laatste wijziging in de vault */
    lastModified?: string;
}

export interface MFilesSearchResponse {
    results: MFilesSearchResult[];
    /** true = de vault heeft meer treffers dan opgehaald; zoekterm verfijnen */
    moreResults: boolean;
    /** Altijd false voor M-Files: nooit een "nieuw aanmaken"-knop tonen */
    canCreate: boolean;
    /** Config-id om mee te sturen met de link-call */
    configId: string;
}

/**
 * Eén onderwijsmedewerker uit de Edu-V-referentiedata (voor de
 * koppel-dropdown). Edu-V kent hier geen primaire ketenidentifier, dus het
 * id-type hoort bij de waarde en moet meelopen naar employee_user_id_type.
 */
export interface EduVEmployee {
    /** Waarde voor employee_user_id; letterlijk doorgeven, niet tonen */
    id: string;
    /** NEPPI | BPI | eduID | ASI | eckId */
    idType?: string;
    name: string;
    /** Extra onderscheidende velden, nodig bij naamgenoten */
    details?: string;
}

/**
 * Per-gebruiker overrides op een (organisatiebrede) exportconfiguratie.
 * Voor Praktijkdata is dat de eigen medewerker-koppeling; de backend gebruikt
 * die als standaardfilter bij zoeken en bij DOCUMENT-exports.
 */
export interface ExportUserSettings {
    configId: string;
    targetPlatform: string;
    /** Alleen deze velden mogen via PUT gezet worden */
    overridableFields: string[];
    settings: Record<string, unknown>;
}

/** Eén medewerker uit de Praktijkdata-referentiedata (voor de koppel-dropdown) */
export interface PraktijkdataMedewerker {
    id: number;
    name: string;
}

export interface Update {
    id: string;
    /** URL-vriendelijke sleutel, gebruikt in /updates/:slug */
    slug: string;
    title: string;
    summary: string;
    /** Machine-sleutel van de categorie; bepaalt kleur/CSS-class. */
    category: UpdateCategory;
    /**
     * Door de server gelokaliseerd, toonbaar label voor de categorie
     * (bijv. "Announcement"). Additief: valt terug op het eigen label
     * uit CATEGORY_META wanneer de feed dit veld (nog) niet levert.
     */
    categoryLabel?: string;
    /** Kanalen waarop deze update verschijnt; minimaal één. */
    channels: UpdateChannel[];
    /** ISO-datum van publicatie */
    publishedAt: string;
    /** Uitgelicht bericht op de frontpage (max. één tegelijk) */
    featured?: boolean;
    version?: string;
    /** Rijke inhoud voor de detailpagina */
    body: UpdateBlock[];
}
